import { useState, type FormEvent, type ReactNode } from 'react';
import { Save } from 'lucide-react';
import { CONTENT_MODES, type ContentMode } from '@cp/shared';
import { Button, Checkbox, Drawer, Field, Input, Select } from '@/components/ui';
import { useSiteCredentials } from '@/hooks/useCredentials';
import { useSaveContentProfile } from '@/hooks/useContent';
import { toast } from '@/store/toastStore';
import { CONTENT_MODE_HINTS, CONTENT_MODE_LABELS } from '@/utils/content';
import { errorField, errorText } from '@/utils/errors';
import { languageWithFlag } from '@/utils/market';
import type { ContentProfileView } from '@/types/api';

const MODE_OPTIONS = CONTENT_MODES.map((mode) => ({ value: mode, label: CONTENT_MODE_LABELS[mode]!, hint: CONTENT_MODE_HINTS[mode]! }));
const NO_LOGIN = 'none';

const FIELD_ERRORS: Record<string, string> = {
  topic: 'От 10 до 3000 символов',
  perWeek: 'От 1 до 21',
  words: 'От 400 до 4000',
  language: 'Код языка: ko, en, pt-BR',
  credentialId: 'Доступ этого сайта с логином и паролем приложения',
};

/**
 * What a site's articles are about and how they go out (ADR-0051). The CMS
 * login is chosen by those who may see logins; for everyone else it stays as set.
 */
export function ContentProfileDrawer({
  siteId,
  profile,
  siteLanguage,
  mayChooseLogin,
  onClose,
}: {
  readonly siteId: string;
  readonly profile: ContentProfileView | null;
  readonly siteLanguage: string | null;
  readonly mayChooseLogin: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const [enabled, setEnabled] = useState(profile?.enabled ?? false);
  const [mode, setMode] = useState<ContentMode>(profile?.mode ?? 'review');
  const [perWeek, setPerWeek] = useState(String(profile?.perWeek ?? 3));
  const [topic, setTopic] = useState(profile?.topic ?? '');
  const [tone, setTone] = useState(profile?.tone ?? '');
  const [keywords, setKeywords] = useState((profile?.keywords ?? []).join(', '));
  const [words, setWords] = useState(String(profile?.words ?? 1200));
  const [research, setResearch] = useState(profile?.research ?? true);
  const [sourcesList, setSourcesList] = useState(profile?.sourcesList ?? true);
  const [language, setLanguage] = useState(profile?.language ?? '');
  const [credentialId, setCredentialId] = useState(profile?.credentialId ?? NO_LOGIN);
  const [category, setCategory] = useState(profile?.category ?? '');

  const credentials = useSiteCredentials(siteId, mayChooseLogin);
  const logins = (credentials.data ?? []).filter((credential) => credential.kind === 'cms' && credential.hasPassword);
  const save = useSaveContentProfile(siteId, {
    onSuccess: () => {
      toast.success('Профиль сохранён');
      onClose();
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate({
      enabled,
      mode,
      perWeek: Number(perWeek),
      topic: topic.trim(),
      tone: tone.trim(),
      keywords: keywords.split(/[,\n]+/).map((item) => item.trim()).filter(Boolean),
      words: Number(words),
      research,
      sourcesList,
      language: language.trim() || null,
      credentialId: credentialId === NO_LOGIN ? null : credentialId,
      category: category.trim() || null,
    });
  };

  const field = errorField(save.error);
  const error = (name: string) => (field === name ? { error: FIELD_ERRORS[name] ?? errorText(save.error) } : {});

  return (
    <Drawer
      open
      title="Профиль контента"
      description="О чём сайт и как выходят статьи"
      onClose={onClose}
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" icon={Save} busy={save.isPending} type="submit" form="content-profile">
            Сохранить
          </Button>
        </>
      }
    >
      <form id="content-profile" className="stack" onSubmit={submit}>
        <Field label="О чём сайт и для кого" htmlFor="content-topic" hint="Как объяснили бы новому автору: тематика, читатели, чего избегать" {...error('topic')}>
          <textarea id="content-topic" className="input" rows={4} value={topic} onChange={(event) => setTopic(event.target.value)} />
        </Field>
        <div className="form-grid">
          <Field label="Стиль" htmlFor="content-tone" hint="Например: спокойный, фактический">
            <Input id="content-tone" value={tone} onChange={(event) => setTone(event.target.value)} />
          </Field>
          <Field label="Язык статей" htmlFor="content-language" hint={language.trim() ? (languageWithFlag(language.trim()) ?? 'Код языка') : siteLanguage ? `Как у сайта: ${languageWithFlag(siteLanguage) ?? siteLanguage}` : 'У сайта не задан язык'} {...error('language')}>
            <Input id="content-language" className="mono" value={language} onChange={(event) => setLanguage(event.target.value)} placeholder={siteLanguage ?? 'ko'} maxLength={10} />
          </Field>
        </div>
        <Field label="Ключевые слова" htmlFor="content-keywords" hint="Через запятую или с новой строки">
          <textarea id="content-keywords" className="input" rows={2} value={keywords} onChange={(event) => setKeywords(event.target.value)} />
        </Field>
        <div className="form-grid">
          <Field label="Длина, слов" htmlFor="content-words" hint="Для японского, китайского и корейского — символов ×2" {...error('words')}>
            <Input id="content-words" type="number" min={400} max={4000} step={100} value={words} onChange={(event) => setWords(event.target.value)} />
          </Field>
          <Field label="Рубрика" htmlFor="content-category" hint="Пусто — Claude выберет из рубрик сайта">
            <Input id="content-category" value={category} onChange={(event) => setCategory(event.target.value)} />
          </Field>
        </div>
        <Checkbox checked={research} onChange={setResearch} label="Искать материал в интернете" hint="Статья опирается на свежие источники; без поиска — только общие сведения" />
        <Checkbox checked={sourcesList} onChange={setSourcesList} label="Список источников в конце статьи" />

        <Field label="Что делать с написанной статьёй">
          <Select label="Режим" value={mode} options={MODE_OPTIONS} onChange={setMode} />
        </Field>
        <Field label="Доступ к CMS" hint="Доступ вида «CMS» с паролем приложения WordPress" {...error('credentialId')}>
          {mayChooseLogin ? (
            <Select
              label="Доступ к CMS"
              value={credentialId}
              options={[{ value: NO_LOGIN, label: 'Не выбран' }, ...logins.map((login) => ({ value: login.id, label: `${login.label}${login.login ? ` · ${login.login}` : ''}` }))]}
              onChange={setCredentialId}
            />
          ) : (
            <span className="muted">{profile?.credentialId ? 'Задан' : 'Не задан'} · выбирает владелец или администратор</span>
          )}
        </Field>

        <Checkbox checked={enabled} onChange={setEnabled} label="Автопилот" hint="Сам подбирает темы и пишет статьи до нужного числа в неделю" />
        <Field label="Статей в неделю" htmlFor="content-per-week" {...error('perWeek')}>
          <Input id="content-per-week" type="number" min={1} max={21} value={perWeek} onChange={(event) => setPerWeek(event.target.value)} />
        </Field>

        {save.isError && !field ? <p className="field-error">{errorText(save.error)}</p> : null}
      </form>
    </Drawer>
  );
}
